import { type GridValidRowModel, type GridSortItem } from '@mui/x-data-grid'
import { type DataObjectList } from '../../models/DataObject'
import { type GenericButtonProps } from '../GenericButton'
import { type ReactNode } from 'react'
import { type DropdownItemConfig } from '../Dropdown'

export interface ApplyFiltersAndDispatchParams extends Record<string, any> {
  pageSize?: number
  pageNumber?: number
  search?: string
  sort?: GridSortItem | Record<string, any>
  scope?: 'my' | 'all'
}

export enum FieldType {
  LINK = 'Link',
  CHIP = 'Chip',
  MULTIPLE_LINK = 'MultipleLink',
  TEXT_LIST = 'TextList',
  OBJECT_LIST = 'ObjectList',
  TEXT = 'Text',
  DATE = 'Date',
  NUMBER = 'Number',
  LINKED_ENTITY = 'LinkedEntity'

}

export enum Actions {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  ARCHIVE = 'ARCHIVE',
  SURVEY_PREVIEW = 'SURVEY_PREVIEW',
  GO_LIVE = 'GO_LIVE',
  READ = 'READ',
  CREATE = 'CREATE',
  OPT_OUT = 'OPT_OUT',
  OPT_OUT_INSTRUCTOR = 'OPT_OUT_INSTRUCTOR',
  // The permission behind the status transitions (go live / close / reopen / template revision).
  // Unlike its per-row consequences below, this one appears in a list's top-level `actions`,
  // because the sections list derives that array from the caller's own permissions.
  CHANGE_STATUS = 'CHANGE_STATUS',
  REOPEN_SURVEY = 'REOPEN_SURVEY',
  CLOSE_SURVEY = 'CLOSE_SURVEY',
  UPDATE_TEMPLATE_REVISION = 'UPDATE_TEMPLATE_REVISION'
}

export enum TableModalAction {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  ARCHIVE = 'ARCHIVE',
  ERROR = 'ERROR',
  SURVEY_PREVIEW = 'SURVEY_PREVIEW',
  GO_LIVE = 'GO_LIVE',
  READ = 'READ',
  OPT_OUT = 'OPT_OUT',
  REOPEN_SURVEY = 'REOPEN_SURVEY',
  CLOSE_SURVEY = 'CLOSE_SURVEY',
  UPDATE_TEMPLATE_REVISION = 'UPDATE_TEMPLATE_REVISION'
}

export interface CustomColumnDef extends GridValidRowModel {
  alwaysShow?: boolean
  shown?: boolean
}

export type Row = Record<string, any>
export type ActionOnChildren = React.ReactElement<{ handleCloseModal: () => void }> | JSX.Element
export type ScopeType = 'my' | 'all'

export interface INoDataComponentProps {
  title?: string
  subTitle?: string
  icon?: React.ReactNode
}

/**
 * A feature-supplied row menu item, gated on a row action the backend sent.
 *
 * Keeps `ActionsWithDropdown` domain-agnostic: the shared component decides whether the
 * row carries `action`, the feature owns the label and what clicking it does. Used by the
 * Activity Monitor, where the backend's `EDIT` on a task row means "this task can be
 * stopped".
 */
export interface ExtraRowAction {
  /** Only rendered when the row's `actions` include this. */
  action: Actions
  label: string
  onClick: (row: Row) => void
  /** Render in the destructive colour used by Delete and Close Survey. */
  destructive?: boolean
}

export interface CustomTableProps<RowType extends Row> {
  list?: DataObjectList<Row> | null | undefined
  columns?: CustomColumnDef[]
  rows?: RowType[]
  label?: string
  createActionTitle?: string
  createAction?: ActionOnChildren
  editAction?: ActionOnChildren
  hideEditColumn?: boolean
  deleteAction?: ActionOnChildren
  errorAction?: ActionOnChildren
  archiveAction?: ActionOnChildren
  reopenSurveyAction?: ActionOnChildren
  closeSurveyAction?: ActionOnChildren
  goLiveAction?: ActionOnChildren
  updateTemplateRevisionAction?: ActionOnChildren
  // Row actions to render disabled (greyed) with a tooltip across every row (e.g. while a background task runs).
  disabledRowActions?: Actions[]
  disabledRowActionTooltip?: string
  // `warningMessage` keys that belong to the whole row rather than to a column. They render on
  // the first column, in the same icon as that column's own warning.
  rowLevelWarningKeys?: string[]
  // Suppress the inline "Edit" affordance for tables that have no edit modal, so a row
  // carrying only READ shows an empty actions menu instead of a dead Edit button.
  hideRowEditAction?: boolean
  // Feature-supplied row menu items, each shown only when the row carries its action.
  extraRowActions?: ExtraRowAction[]
  topbarTitle?: string
  enableAllSelection?: boolean
  hideSelectedColumnCount?: boolean
  defaultSort?: GridSortItem[] | undefined
  featureName?: string
  extraColumnProps?: object | undefined
  onEditClick?: (row: Row) => void
  onPreviewClick?: (row: Row) => void
  onRowClick?: (row: Row) => void
  onOptOutClick?: (row: Row) => void
  onOptOutInstructorClick?: (row: Row) => void
  customTopbarAction?: React.ReactNode
  overrides?: Record<string, (a: any) => JSX.Element>
  /** Per-field column-header renderers, keyed by field. Receives the column's headerName. */
  headerOverrides?: Record<string, (headerName: string) => JSX.Element>
  createButtonProps?: object | undefined
  onSearch?: (value: string) => void
  // Drop the search box for lists whose endpoint has no search.
  hideSearch?: boolean
  // Replaces the shared row tooltip (metadata / "Double click to open") with feature content.
  // Receives the CDG row's `data`; return null for no tooltip on that row.
  getRowTooltip?: (rowData: Row) => ReactNode
  // Pointer cursor on every row, for tables whose `onRowClick` navigates.
  clickableRows?: boolean
  // Show the "No results" empty state, as a search does, when the feature's own filters are set.
  hasActiveFilters?: boolean
  handlePageChange?: (page: number) => Promise<void> | void
  handleRowsPerPageChange?: (page: number) => Promise<void> | void
  loading?: boolean
  noDataComponentProps?: INoDataComponentProps
  onSortModelChange?: (sortModel: GridSortItem) => Promise<void> | void
  actionModalEntityDisplayField?: string
  createModalProps?: object | undefined
  editModalProps?: object | undefined
  deleteModalProps?: object | undefined
  errorModalProps?: object | undefined
  extraModalProps?: object | undefined
  archiveModalProps?: object | undefined
  reopenSurveyModalProps?: object | undefined
  exportCsv?: () => void
  exportCsvLoading?: boolean
  exportButtonProps?: GenericButtonProps
  params?: Record<string, any>
  setParams?: (params: Record<string, any>) => void
  showScopeToggle?: boolean
  scopeToggleTitle?: string
  topbarActionsDropdownMenu?: DropdownItemConfig[] | ReactNode
  hideActionsColumn?: boolean
  disableRowDoubleClick?: boolean
}
